<template>
  <delivery-tab-layout :loading="isPointsLoading" class="delivery-pickup-tab">
    <slot />

    <vue-client-only>
      <m-pickup-filters
        v-if="!adaptive.isDesktop"
        v-model="filters"
        class="delivery-pickup-tab__filters"
      />
    </vue-client-only>

    <h2 class="delivery-pickup-tab__subtitle">
      <span>{{ pointsCountText }} самовывоза</span>

      <vue-client-only>
        <ui-segmented
          v-if="!adaptive.isDesktop"
          v-model="viewType"
          :labels="Object.values(viewTypes)"
        />
      </vue-client-only>
    </h2>

    <vue-client-only>
      <div
        v-if="viewType === viewTypes.list.value || adaptive.isDesktop"
        ref="pickup-list"
        class="delivery-pickup-tab__list"
      >
        <template v-if="filteredPoints.length">
          <pickup-list-item
            v-for="point of filteredPoints.slice(0, pointsLimit)"
            :key="point.id"
            :store="point"
            :ids="lineIds"
            :distance="calcDistanceToUser(point.geo)"
            class="delivery-pickup-tab__item"
            @select="changeActivePoint(point.id)"
          />

          <span v-reached="increaseLimit" class="delivery-pickup-tab__reached" />
        </template>

        <page-info
          v-else
          class="delivery-pickup-tab__stub"
          title="По вашему запросу ничего не&nbsp;найдено"
        >
          Попробуйте изменить условия поиска или выберите магазин
          <span @click="resetFilters">из&nbsp;списка</span>
        </page-info>
      </div>
    </vue-client-only>

    <template #aside>
      <vue-client-only>
        <d-pickup-filters
          v-if="adaptive.isDesktop"
          v-model="filters"
          class="delivery-pickup-tab__filters"
        />

        <stores-map
          v-if="viewType === viewTypes.map.value || adaptive.isDesktop"
          :current-local-city="currentCity"
          :active-store-id="activePointId"
          :stores="filteredPoints"
          class="delivery-pickup-tab__map"
          @select="changeActivePoint"
        />
      </vue-client-only>
    </template>

    <template #append>
      <pickup-store-card
        :store="activePoint"
        :products="products"
        :ids="lineIds"
        :distance="calcDistanceToUser(activePoint.geo)"
        @back="changeActivePoint"
        @select="onSelectStore"
        @close="$emit('close')"
      />
    </template>
  </delivery-tab-layout>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import VueClientOnly from 'vue-client-only';
import { pluralize } from '@/utils/string-utils';
import { LOCKDOWN_MODE } from '@/consts';
import { cartGtmEvents } from '@/gtm/cart-gtm-events';
import { defaultFilters } from './pickup-filters/constants';
import { checkDayAndNight, getLineIds } from './utils';

import PageInfo from '@/components/common/page-info.vue';
import DPickupFilters from './pickup-filters/d-pickup-filters.vue';
import StoresMap from '@/components/stores/stores-map.vue';
import PickupStoreCard from './pickup-store-card.vue';
import PickupListItem from './pickup-list-item.vue';
import DeliveryTabLayout from './delivery-tab-layout.vue';
import MPickupFilters from './pickup-filters/m-pickup-filters.vue';

import IconList from '@icons/list.svg';
import IconMap from '@icons/map.svg';

export const VIEW_TYPES = {
  list: {
    value: 'list',
    icon: IconList.id,
  },
  map: {
    value: 'map',
    icon: IconMap.id,
  },
};

export default {
  name: 'delivery-pickup-tab',

  components: {
    DeliveryTabLayout,
    MPickupFilters,
    PageInfo,
    PickupListItem,
    PickupStoreCard,
    VueClientOnly,
    StoresMap,
    DPickupFilters,
  },

  inject: ['adaptive'],

  props: {
    currentCity: {
      type: Object,
      default: () => ({}),
    },

    products: {
      type: Array,
      default: () => [],
    },
    pointId: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    viewType: VIEW_TYPES.list.value,
    activePointId: '',
    points: [],
    filters: { ...defaultFilters },
    pointsLimit: 20,
    isPointsLoading: false,
    isMounted: false,
  }),

  computed: {
    ...mapState({
      userGeoCoords: state => state.user.geoCoords,
    }),
    ...mapGetters({
      CALC_DISTANCE_TO_USER: 'user/calcDistanceToUser',
    }),

    viewTypes: () => VIEW_TYPES,

    pointsCountText() {
      return pluralize(this.filteredPoints.length, ['пункт', 'пункта', 'пунктов']);
    },

    lineIds() {
      return this.getLineIds();
    },

    activePoint() {
      return this.filteredPoints.find(p => p.id === this.activePointId) || {};
    },

    filteredPoints() {
      const query = this.filters.query?.toLowerCase();
      // массив выбранных типов пунктов в формате от бека
      const pointTypes = ['postamat', 'store', 'pvz'].filter(type =>
        type === 'postamat' && this.filters.terminal
        || type === 'store' && this.filters.store
        || type === 'pvz' && this.filters.pickup,
      );
      // TODO: Отрефакторить выносом условий в переменные в рамках ESMFUN-899
      return this.points
        .filter(point =>
          (!pointTypes.length || pointTypes.includes(point.type))
          && (this.filters.fitting ? point.services?.includes('has_fitting') : true)
          && (this.filters.withoutPrepay ? !point.needPrepay : true)
          && (this.filters.withoutQrCode ? !point.workMode || point.workMode?.toLowerCase() === 'магазин' : true)
          && (this.filters.returns && point.type !== 'store' ? point.services?.includes('has_refund') : true)
          && (this.filters.dayAndNight ? checkDayAndNight(point.workSchedule) : true)
          && (this.filters.cardPay ? point.services?.includes('cash_and_card') : true)
          && (query.length ? `${point.metro.map(({ name }) => name).join('')}${point.address}`.toLowerCase().includes(query) : true)
          && (point.type === 'store' ? point.status === 'open' : true), // отображать только открытые магазины
        )
        // необходимо выводить первыми те пункты, где доступны для самовывоза все вещи
        .sort((a, b) => b.availableLinesCount - a.availableLinesCount);
    },

    storeSorting() {
      const sort = ['order:store'];

      if (this.userGeoCoords) {
        const { latitude, longitude } = this.userGeoCoords;
        sort.push(`location:${latitude},${longitude}`);
      } else {
        sort.push('availableLinesCount:desc');
      }

      return sort.join(';');
    },
  },

  watch: {
    filteredPoints() { // сброс лимита при изменении параметров фильтра
      if (this.$refs['pickup-list']) this.$refs['pickup-list'].scrollTo({ top: 0 });
      this.pointsLimit = 20;

      if (this.isMounted && this.activePointId) this.activePointId = '';
    },

    currentCity() {
      this.getPickupPoints();
    },
  },

  mounted() {
    this.getPickupPoints().finally(async () => {
      this.activePointId = this.pointId;

      // ждем пока получим начальный список, чтобы случайно начальный список не пришел позже
      // и не заменил отсортированный по геопозиции
      await this.GET_USER_GEO_COORDS();

      // обновляем список пунктов самовывоза незаметно для пользователя (без сброса фильтров и прелоадера)
      if (this.userGeoCoords) await this.getPickupPoints({ silent: true });
      this.isMounted = true;
    });
  },

  methods: {
    ...mapActions({
      SET_SHIPPING_GROUP: 'cart/setShippingGroup',
      GET_USER_GEO_COORDS: 'user/getGeoCoords',
    }),

    getPickupPoints({ silent = false } = {}) {
      if (!silent) {
        this.resetFilters();
        this.points = [];
      }
      if (!this.lineIds.length) return Promise.resolve();
      this.isPointsLoading = !silent;

      return this.$store.$api.getPickupPoints({
        cityId: this.currentCity?.id,
        lineIds: this.lineIds,
        sort: this.storeSorting,
      }).then(points => {
        this.points = LOCKDOWN_MODE
          ? points.map(point => point.services?.length ? { ...point, services: [] } : point)
          : points;
      }).finally(() => {
        this.isPointsLoading = false;
      });
    },

    changeActivePoint(id) {
      this.activePointId = id || '';
    },

    getLineIds(products = this.products) {
      return getLineIds(products);
    },

    increaseLimit() {
      this.pointsLimit += 10;
    },

    resetFilters() {
      this.filters = { ...defaultFilters };
    },

    onSelectStore() { // возвращает выбранный магазин в checkout
      this.SET_SHIPPING_GROUP({
        type: this.activePoint.type === 'store' ? 'pickup' : 'thirdparty',
        value: this.activePoint.id,
        lineIds: this.lineIds, // ххххм, а если не все есть в точке???
      }).then(() => {
        this.$f.$emit('select-pickup');
        cartGtmEvents.shippingByPickupStore();
      });
      this.$emit('close');
    },

    calcDistanceToUser(geo) {
      const distance = this.CALC_DISTANCE_TO_USER(geo);
      return distance ? `${distance} км`.replace('.', ',') : '';
    },
  },
};
</script>

<style lang="scss">
.delivery-pickup-tab {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__subtitle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 40px;
    margin: 0 0 16px;
    font-family: Gilroy, sans-serif;
    font-weight: 600;
    font-size: 18px;
    line-height: 20px;

    & > span {
      max-width: 156px;
    }
  }

  &__stub {
    max-width: 288px;

    .title {
      font-size: 16px;
      line-height: 24px;
    }

    .description {
      padding-top: 4px;
      font-size: 14px;
      line-height: 20px;

      span {
        color: $blue;
        cursor: pointer;
      }
    }
  }

  &__reached {
    position: relative;
    display: block;
    bottom: 200px;
  }

  &__filters {
    margin-bottom: 16px;
  }

  &__map {
    width: 100vw;
    margin-left: -16px;
    height: 480px;
  }

  &__aside {
    flex: 1;
    padding-bottom: 32px;
  }

  &__loader {
    display: flex;
    width: 100%;
    margin-top: 20%;
    align-items: center;
    justify-content: center;
    color: $blue;
  }
}

@include breakpoint("tablet") {
  .delivery-pickup-tab {
    &__subtitle {
      & > span {
        max-width: none;
      }
    }

    &__filters {
      margin-top: 24px;
    }

    &__list {
      border-top: 1px solid $gray_lu;
    }

    &__item {
      padding: 12px;
    }

    &__map {
      width: auto;
      min-height: 604px;
      height: 100%;
      margin: 0;
    }

    &__aside {
      padding-top: 4px;
    }
  }
}

@include breakpoint("desktop") {
  .delivery-pickup-tab {
    flex-direction: row;
    width: 100%;

    &__content {
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
      width: 536px;
      margin-right: 32px;
      overflow: hidden;
    }

    &__aside {
      position: relative;
      width: 100%;
      padding: 0;
    }

    &__subtitle {
      height: 24px;
    }

    &__filters {
      position: absolute;
      z-index: 1;
      padding: 10px 10px 0;
      margin: 0;
    }

    &__list {
      height: 100%;
      overflow: auto;
    }

    &__map {
      min-height: auto;
      height: 100%;
      border-top-left-radius: 4px;
      overflow: hidden;
    }

    &__loader {
      margin-top: 0;
    }
  }
}
</style>
